import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

interface EmployerCategory {
	id: number;
	uuid: string;
	name: string;
}

interface Sector {
	id: number;
	uuid: string;
	name: string;
}

interface Person {
	fullName: string | null;
	email: string | null;
	mobile: string | null;
	landline: string | null;
	address: string | null;
	cnic: string | null;
}

interface BusinessDetails {
	id: number;
	uuid: string;
	businessName: string;
	phoneNumber: string;
	address: string;
	businessWebsiteURL: string | null;
	businessPrincipalLine: string | null;
	businessEmailAddress: string | null;
	employerType: string | null; // "national" or "international"
	ntn: string | null;
	certificateOfIncorporation: string | null;
	employerCategory: EmployerCategory | null;
	sector: Sector | null;
	person: Person | null;
}

interface User {
	id: string;
	uuid?: string;
	emailAddress: string;
	phoneNumber: string | null;
	firstName: string | null;
	lastName: string | null;
	cnic: string | null;
	isActive: boolean;
	isVerified?: boolean;
	isProfileCompleted?: boolean;
	role?: string;
	createdAt: string;
	updatedAt: string;
}

interface UserState {
	user: User | null;
	token: string | null;
	isAuthenticated: boolean;
	expireTimer: string | number;
	resetToken: string | null;
	businessDetails: BusinessDetails | null;
}

const initialState: UserState = {
	user: null,
	token: null,
	isAuthenticated: false,
	expireTimer: "",
	resetToken: null,
	businessDetails: null,
};

const userSlice = createSlice({
	name: "user",
	initialState,
	reducers: {
		setUser: (state, action: PayloadAction<{ user: User; token: string; businessDetails?: BusinessDetails | null }>) => {
			state.user = action.payload.user;
			state.token = action.payload.token;
			state.isAuthenticated = true;
			if (action.payload.businessDetails !== undefined) {
				state.businessDetails = action.payload.businessDetails;
			}
		},
		setBusinessDetails: (state, action: PayloadAction<BusinessDetails | null>) => {
			state.businessDetails = action.payload;
		},
		clearUser: state => {
			state.user = null;
			state.token = null;
			state.isAuthenticated = false;
			state.businessDetails = null;
		},
		setExpireTimer: (state, action: PayloadAction<string>) => {
			state.expireTimer = action.payload;
		},
		setResetToken: (state, action: PayloadAction<string>) => {
			state.resetToken = action.payload;
		},
		initializeUser: (state, action: PayloadAction<{ user: User | null; token: string | null; businessDetails?: BusinessDetails | null }>) => {
			state.user = action.payload.user;
			state.token = action.payload.token;
			state.isAuthenticated = !!action.payload.token;
			if (action.payload.businessDetails !== undefined) {
				state.businessDetails = action.payload.businessDetails;
			}
		},
	},
});

export const { setUser, setBusinessDetails, clearUser, setExpireTimer, setResetToken, initializeUser } = userSlice.actions;
export default userSlice.reducer;
